import { Button } from "antd";
import { useNavigate } from "react-router-dom";
import { formatPrice } from "@/shared/components/mission-setting/utils/format-price";
import { convertToJalali } from "@/shared/components/calendarJalali/utils/convert-to-jalali";
import { PaymentResultData } from "@/features/payment/wallet/domain/payment.interface";
import { PaymentResultStatusType } from "@/features/payment/wallet/domain/payment.type";
import { paymentStatusMetaMap } from "@/features/payment/wallet/utils/payment-result-status";

interface BasePaymentResultCardProps {
  paymentResultType: PaymentResultStatusType;
  paymentResult: PaymentResultData;
  paymentTrackingRoute?: string;
}

interface PaymentResultCardWithButton extends BasePaymentResultCardProps {
  handlePaymentResult: () => void;
  handleButtonText: string;
}

interface PaymentResultCardWithoutButton extends BasePaymentResultCardProps {
  handlePaymentResult?: undefined;
  handleButtonText?: undefined;
}

type PaymentResultCardProps =
  | PaymentResultCardWithButton
  | PaymentResultCardWithoutButton;

const PaymentResultCard = ({
  paymentResultType,
  paymentResult,
  paymentTrackingRoute,
  handleButtonText,
  handlePaymentResult,
}: PaymentResultCardProps) => {
  const navigate = useNavigate();

  const { label, description, buttonText, bgColor, backImg } =
    paymentStatusMetaMap[paymentResultType];

  function trackPaymentStatus() {
    if (paymentTrackingRoute) navigate(paymentTrackingRoute);

    if (handlePaymentResult) handlePaymentResult();
  }

  return (
    <div className="Paymentsection flex flex-col justify-center items-center z-[900] w-full h-[100vh] absolute top-0 left-0">
      <div className="Borderout flex flex-col justify-center items-center rounded-[40px] neo overflow-hidden bg-[#E9F0F7] p-1">
        <div className="Borderin flex flex-col justify-center items-center rounded-[40px] shadow-inner overflow-hidden bg-[#E9F0F7] p-1">
          <div
            id="divToPrint"
            className={` flex flex-col justify-center items-center rounded-[40px] w-[70vw] max-lg:w-[92vw] h-[75vh] p-4   `}
            style={{ backgroundColor: bgColor }}
          >
            <div className="Borderdashded flex flex-col justify-center items-center py-8 rounded-3xl border-[4px] border-dashed border-white w-full h-full gap-4 max-lg:border-none">
              <p className=" text-center text-white text-[44px] max-lg:text-[1.5rem] font-bold">
                {label}
              </p>

              <div className="Borderdashded flex flex-col justify-center items-center gap-1">
                <p className=" text-center text-white text-[20px] py-2 font-semibold">
                  {description}
                </p>

                <div className="Text flex gap-[12rem] max-lg:gap-[1rem] justify-start items-center rounded-[10px] w-full h-[40px] bg-[#ffffff60] p-4">
                  <p className=" text-right text-[#333] w-[160px] max-lg:w-[110px] text-[20px] max-lg:text-sm font-semibold">
                    مبلغ:
                  </p>

                  <p className=" text-center text-[#000] text-[18x] max-lg:text-xs whitespace-nowrap font-medium">
                    {formatPrice(paymentResult.amount)}
                    تومان
                  </p>
                </div>

                <div className="Text flex gap-[12rem] max-lg:gap-[1rem] justify-start items-center rounded-[10px] w-full h-[40px] bg-[#ffffff60] p-4">
                  <p className=" text-right text-[#333] w-[160px] max-lg:w-[110px] text-[20px] max-lg:text-sm font-semibold">
                    شرح پرداخت:
                  </p>

                  <p className=" text-center text-[#000] text-[18x] max-lg:text-xs whitespace-nowrap font-medium">
                    {paymentResult.subject}
                  </p>
                </div>

                <div className="Text flex gap-[12rem] max-lg:gap-[1rem] justify-start items-center rounded-[10px] w-full h-[40px] bg-[#ffffff60] p-4">
                  <p className=" text-right text-[#333] w-[160px] max-lg:w-[110px] text-[20px] max-lg:text-sm font-semibold">
                    نوع پرداخت:
                  </p>

                  <p className=" text-center text-[#000] text-[18x] max-lg:text-xs whitespace-nowrap font-medium">
                    {paymentResult.type}
                  </p>
                </div>

                <div className="Text flex gap-[12rem] max-lg:gap-[1rem] justify-start items-center rounded-[10px] w-full h-[40px] bg-[#ffffff60] p-4">
                  <p className=" text-right text-[#333] w-[160px] max-lg:w-[110px] text-[20px] max-lg:text-sm font-semibold">
                    شناسه پیگیری:
                  </p>

                  <p className=" text-center text-[#000] text-[18x] max-lg:text-xs whitespace-nowrap font-medium">
                    {paymentResult.trackingCode}
                  </p>
                </div>

                <div className="Text flex gap-[12rem] max-lg:gap-[1rem] justify-start items-center rounded-[10px] w-full h-[40px] bg-[#ffffff60] p-4">
                  <p className=" text-right text-[#333] w-[160px] max-lg:w-[110px] text-[20px] max-lg:text-sm font-semibold">
                    شناسه مرجع بانک:
                  </p>

                  <p className=" text-center text-[#000] text-[18x] max-lg:text-xs whitespace-nowrap font-medium">
                    {paymentResult.referenceBankId ?? "-"}
                  </p>
                </div>

                <div className="Text flex gap-[12rem] max-lg:gap-[1rem] justify-start items-center rounded-[10px] w-full h-[40px] bg-[#ffffff60] p-4">
                  <p className=" text-right text-[#333] w-[160px] max-lg:w-[110px] text-[20px] max-lg:text-sm font-semibold">
                    تاریخ درخواست:
                  </p>

                  <p className=" text-center text-[#000] text-[18x] max-lg:text-xs whitespace-nowrap font-medium">
                    {convertToJalali(paymentResult.date)}
                  </p>
                </div>
              </div>

              <img className="absolute opacity-60" alt="check" src={backImg} />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-row gap-4">
        {/* <Button
          onClick={printDocument}
          className="button-neo mt-8 px-16 h-10 z-50 max-lg:mt-16"
        >
          دریافت رسید
        </Button> */}
        <Button
          onClick={trackPaymentStatus}
          className="button-green mt-8 px-16 h-10 z-50 max-lg:mt-16 text-white rounded-full"
        >
          {handleButtonText ?? buttonText}
        </Button>
      </div>
    </div>
  );
};

export default PaymentResultCard;
